import { useState, useEffect, useRef, useCallback } from 'react';
import { useOnboardingVideoEvent } from './analytics/useOnboardingVideoEvent';

const STORAGE_KEY = 'onboardingVideoSeen';

function getQueryParam(key: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return new URLSearchParams(window.location.search).get(key);
}

/**
 * Manages the onboarding video modal visibility and persistence.
 * - Auto-opens once per device if user hasn't seen it yet.
 * - Saves "seen" flag in localStorage on every open.
 * - Tracks Segment events on open (rendered), manual open (Learn More) and close.
 */
export function useOnboardingModalTrigger() {
  const { trackLearnMore, trackModalOpened, trackModalClosed } = useOnboardingVideoEvent();

  const [isOpen, setIsOpen] = useState(false);
  const hasAutoOpened = useRef(false);

  const rawPlatform = getQueryParam('platform') || 'web';
  const platform = rawPlatform === 'ios' || rawPlatform === 'android' ? 'mobile' : 'web';

  // Auto-open effect: only once, only if pro has no positions, and only if not seen before
  useEffect(() => {
    const seen = typeof window !== 'undefined' && localStorage.getItem(STORAGE_KEY) === 'true';
    if (!hasAutoOpened.current && !seen) {
      setIsOpen(true);
      trackModalOpened({ platform });
      localStorage.setItem(STORAGE_KEY, 'true');
      hasAutoOpened.current = true;
    }
  }, [platform, trackModalOpened]);

  // Persist "seen" flag whenever modal opens (manual or auto)
  useEffect(() => {
    if (isOpen && typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  }, [isOpen]);

  const open = useCallback(() => {
    trackLearnMore({ platform });
    trackModalOpened({ platform });
    setIsOpen(true);
  }, [platform, trackLearnMore, trackModalOpened]);

  const close = useCallback(() => {
    trackModalClosed({ platform });
    setIsOpen(false);
  }, [platform, trackModalClosed]);

  return { isOpen, open, close };
}
